import React, { useEffect, useState } from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Typography
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import packageJson from '../../../../package.json';

const typeConfig = {
    'Added': { bg: '#E3F2FD', color: '#1565C0', label: 'New' },
    'Fixed': { bg: '#FFF3E0', color: '#E65100', label: 'Fix' },
    'Improved': { bg: '#E8F5E9', color: '#2E7D32', label: 'Improved' },
    'Changed': { bg: '#F3E5F5', color: '#7B1FA2', label: 'Changed' },
    'Removed': { bg: '#FFEBEE', color: '#C62828', label: 'Removed' },
};

function parseChangelog(markdown) {
    const entries = [];
    const lines = markdown.split('\n');
    let currentEntry = null;
    let currentType = null;

    for (const line of lines) {
        // Match version header: ## [2.4.0] - 2026-01-17
        const versionMatch = line.match(/^## \[(.+?)\] - (\d{4}-\d{2}-\d{2})/);
        if (versionMatch) {
            if (currentEntry) {
                entries.push(currentEntry);
            }
            currentEntry = {
                version: versionMatch[1],
                date: versionMatch[2],
                changes: []
            };
            currentType = null;
            continue;
        }

        // Match section header: ### Added, ### Fixed, etc.
        const sectionMatch = line.match(/^### (.+)/);
        if (sectionMatch) {
            currentType = sectionMatch[1];
            continue;
        }

        // Match change item: - Some change description
        const itemMatch = line.match(/^- (.+)/);
        if (itemMatch && currentEntry && currentType) {
            currentEntry.changes.push({
                type: currentType,
                description: itemMatch[1]
            });
        }
    }

    if (currentEntry) {
        entries.push(currentEntry);
    }

    return entries;
}

function ChangelogEntry({ entry }) {
    const formatDate = (dateStr) => {
        const date = new Date(dateStr + 'T00:00:00');
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                {entry.version && (
                    <Typography variant="subtitle1" fontWeight="600">
                        v{entry.version}
                    </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                    {formatDate(entry.date)}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 1 }}>
                {entry.changes.map((change, idx) => {
                    const config = typeConfig[change.type] || typeConfig['Improved'];
                    return (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Chip
                                label={config.label}
                                size="small"
                                sx={{
                                    backgroundColor: config.bg,
                                    color: config.color,
                                    fontWeight: 500,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                    minWidth: '65px',
                                }}
                            />
                            <Typography variant="body2" sx={{ pt: 0.25 }}>
                                {change.description}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

function ChangelogDialog({ open, onClose }) {
    const [changelog, setChangelog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setLoading(true);
            setError(null);
            fetch(`/CHANGELOG.md?v=${encodeURIComponent(packageJson.version)}&t=${Date.now()}`, { cache: 'no-store' })
                .then(res => {
                    if (!res.ok) throw new Error('Failed to load changelog');
                    return res.text();
                })
                .then(text => {
                    const parsed = parseChangelog(text);
                    setChangelog(parsed);
                    setLoading(false);
                })
                .catch(err => {
                    setError(err.message);
                    setLoading(false);
                });
        }
    }, [open]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    maxHeight: '80vh',
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #E0E0E0',
                pb: 1.5,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NewReleasesIcon sx={{ color: '#1976D2' }} />
                    <Typography variant="h6" fontWeight="600">
                        What's New
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2.5 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                    </Box>
                )}
                {error && (
                    <Typography color="error" sx={{ py: 2 }}>
                        {error}
                    </Typography>
                )}
                {!loading && !error && changelog.map((entry, idx) => (
                    <React.Fragment key={idx}>
                        <ChangelogEntry entry={entry} />
                        {idx < changelog.length - 1 && (
                            <Divider sx={{ mb: 2.5 }} />
                        )}
                    </React.Fragment>
                ))}
            </DialogContent>
        </Dialog>
    );
}

export default ChangelogDialog;
